import type { RouteMap } from "#vue-router"
import type {
  Provider,
  OnboardingStepName,
  Workspace,
} from "~/types/api/workspace"
import { isBefore, isValid, parseISO, startOfDay } from "date-fns"

export type WorkspaceOnboardingStep = {
  link: {
    name: (typeof stepLinkNames)[keyof typeof stepLinkNames]
  }
  title: string
  hidden?: boolean
}

export const onboardingSteps = [
  "invite_admins",
  "verify_domain",
  "employee_import",
  "whitelisting",
  "simulation",
  "reporting_add_in",
  "training",
  "schedule_kick_off",
] as const

export const stepLinkNames = {
  welcome: "workspace-onboarding",
  invite_admins: "workspace-onboarding-invite-admins",
  verify_domain: "workspace-onboarding-verify-domains",
  employee_import: "workspace-onboarding-integration",
  whitelisting: "workspace-onboarding-whitelisting",
  simulation: "workspace-onboarding-simulations",
  reporting_add_in: "workspace-onboarding-reporting-add-in",
  training: "workspace-onboarding-training",
  schedule_kick_off: "workspace-onboarding-kick-off",
} as const satisfies Record<OnboardingStepName | "welcome", keyof RouteMap>

/**
 * Standalone functions to check if/how workspace onboarding should be shown.
 *
 * Required for login flow where `useWorkspaceOnboarding` can't be used (it depends on `useWorkspaceSettings` which returns 4xx before authentication).
 */
export function shouldShowWorkspaceOnboarding(workspace?: Workspace): boolean {
  if (!workspace) {
    return false
  }

  const state = workspace.onboarding_state?.state
  const isDemo = workspace.demo
  return (state == "unstarted" || state == "in_progress") && !isDemo
}

export function shouldShowWorkspaceKickoffPlan(
  workspace?: Workspace,
  currentDate = new Date(),
): boolean {
  const onboardingState = workspace?.onboarding_state
  const currentDay = startOfDay(currentDate)

  return (
    onboardingState?.state == "completed" &&
    [
      onboardingState.simulation_date,
      onboardingState.training_kickoff_date,
    ].some((date) => {
      if (!date) return false

      const kickoffDate = parseISO(date)
      return isValid(kickoffDate) && !isBefore(kickoffDate, currentDay)
    })
  )
}

export function getNextIncompleteOnboardingStep(workspace?: Workspace) {
  if (!workspace || !shouldShowWorkspaceOnboarding(workspace)) return

  if (workspace.onboarding_state.state == "unstarted") {
    return { name: "workspace-onboarding" } as const
  }
  const steps = workspace.onboarding_state?.steps

  const nextStep = onboardingSteps.find((step) => steps[step] != "completed")

  return nextStep
    ? ({ name: stepLinkNames[nextStep] } as const)
    : ({ name: "workspace-onboarding" } as const)
}

export function useWorkspaceOnboarding() {
  const { data: workspace, refresh: refreshWorkspace } = useWorkspaceSettings()
  const { t } = useI18n()
  const { hasFeatureAccess: hasReportingAddInAccess } = useFeatureAccess(
    "phishing-reporting-add-in",
  )
  const { hasFeature } = useAuth()

  const completedSteps = computed(() => {
    const steps = workspace.value?.onboarding_state?.steps
    if (!steps) return []

    return onboardingSteps
      .filter((stepState) => steps[stepState] == "completed")
      .map((stepState) => stepLinkNames[stepState])
  })

  const onboardingProvider = useCookie<Provider | undefined>(
    "workspace_onboarding_provider",
  )

  const steps = computed(
    () =>
      [
        {
          link: { name: "workspace-onboarding" },
          title: t("workspace_onboarding.steps.welcome"),
          hidden: true,
        },
        {
          link: { name: "workspace-onboarding-invite-admins" },
          title: t("workspace_onboarding.steps.invite_admins"),
          hidden: !hasFeature("admin:platform:access-role-management"),
        },
        {
          link: { name: "workspace-onboarding-verify-domains" },
          title: t("workspace_onboarding.steps.verify_domains"),
        },
        {
          link: { name: "workspace-onboarding-integration" },
          title: t("workspace_onboarding.steps.setup_provider"),
        },
        {
          link: { name: "workspace-onboarding-whitelisting" },
          title: t("workspace_onboarding.steps.whitelisting"),
        },
        {
          link: { name: "workspace-onboarding-simulations" },
          title: t("workspace_onboarding.steps.add_simulation"),
        },
        {
          link: { name: "workspace-onboarding-reporting-add-in" },
          title: t("workspace_onboarding.steps.reporting_add_in"),
          hidden: !hasReportingAddInAccess.value,
        },
        {
          link: { name: "workspace-onboarding-training" },
          title: t("workspace_onboarding.steps.training"),
        },
        {
          link: { name: "workspace-onboarding-kick-off" },
          title: t("workspace_onboarding.steps.kick_off"),
        },
      ] satisfies WorkspaceOnboardingStep[],
  )

  function isStepCompleted(step: WorkspaceOnboardingStep) {
    return completedSteps.value.some((name) => name == step.link.name)
  }

  const route = useRoute()
  const routeName = computed(() => route.name)
  const currentStepIndex = computed(() => {
    return steps.value.findIndex((step) => step.link.name == routeName.value)
  })

  const currentStep = computed(() => {
    if (currentStepIndex.value == -1) {
      return
    }

    return steps.value[currentStepIndex.value]
  })

  const previousStep = computed(() => {
    for (let index = currentStepIndex.value - 1; index >= 0; index--) {
      const step = steps.value[index]
      if (!step) {
        return
      }

      if (!step.hidden || step.link.name == "workspace-onboarding") {
        return step
      }
    }
  })

  const nextStep = computed(() => {
    for (
      let index = currentStepIndex.value + 1;
      index < steps.value.length;
      index++
    ) {
      const step = steps.value[index]
      if (!step) {
        return
      }

      if (!step.hidden) {
        return step
      }
    }
  })

  const { $api } = useNuxtApp()
  const { mutate: completeStep } = useMutation({
    fetchFunction: async (step: OnboardingStepName) => {
      if (currentStep.value && isStepCompleted(currentStep.value)) {
        return
      }

      return await $api<unknown>("/admin/v1/workspace/onboarding/complete", {
        method: "post",
        body: { step },
      })
    },
    onSuccess: async () => {
      await refreshWorkspace()
      await navigateTo(nextStep.value?.link ?? { name: "dashboard" })
    },
  })

  const { userRole } = useAuth()
  const showOnboarding = computed(() => {
    if (userRole.value != "admin") {
      return false
    }

    return shouldShowWorkspaceOnboarding(workspace.value)
  })

  const showKickoffPlan = computed(() => {
    if (userRole.value != "admin") {
      return false
    }

    return shouldShowWorkspaceKickoffPlan(workspace.value)
  })

  return {
    completedSteps,
    steps,
    currentStep,
    previousStep,
    nextStep,
    isStepCompleted,
    onboardingProvider,
    completeStep,
    refreshWorkspace,
    showOnboarding,
    showKickoffPlan,
  }
}
